/**
 * ProposalCard — one kid-suggested chore awaiting a parent's decision (Fase 3).
 *
 * Shows the suggestion (icon, title, description, who proposed it, cadence) and
 * lets the parent:
 *   • adjust the point value before approving (the kid's suggested value is
 *     pre-filled; the server clamps to ≥1)
 *   • Godkjenn   → POST /proposals/:id/approve with the adjusted points
 *   • Avvis      → reveals an inline reason field → POST /proposals/:id/reject
 *
 * Stateless beyond the local point-adjust + reject-reason draft; the parent
 * route owns the mutation wiring and pending flags.
 *
 * #155 (redesign steg 5a): det store kortet i Godkjenn-køen når forslaget står
 * først. Samme flate som ApprovalCard: forslagsstillerens InitialAvatar og
 * navn, tittel, beskrivelse og gjentakelse, poengene som Stepper (dagens
 * grenser: minst 1, steg 1), og Avvis / Godkjenn på 52 px. Avvisningen er
 * fortsatt den valgfrie begrunnelsen inline.
 */
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Recurrence } from '@stell/shared';
import { Button, Stepper } from '../../components/primitives';
import { Icon } from '../../components/primitives/Icon';
import { InitialAvatar } from '../../components/primitives/InitialAvatar';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { RelativeTime } from '../../components/primitives/RelativeTime';
import type { ChoreTemplateRow } from '../admin/useChoreTemplates';
import type { ApproveProposalOverrides } from './useProposals';
import { FEATURED_CARD } from './queueParts';

export interface ProposalCardProps {
  proposal: ChoreTemplateRow;
  /** Resolved display name of the proposing member; '' if unknown. */
  proposerName: string;
  /** Personfargen til den som foreslo; standard m-sage. */
  proposerSlot?: ColorSlot;
  approving: boolean;
  rejecting: boolean;
  onApprove: (id: string, overrides: ApproveProposalOverrides) => void;
  onReject: (id: string, reason: string) => void;
}

/** Gjentakelsen som tekst («Daglig»), eller '' når JSON-en ikke kan leses. */
export function recurrenceLabel(json: string, t: (k: string) => string): string {
  try {
    const r = JSON.parse(json) as Recurrence;
    return t(`recurrence.types.${r.type}`);
  } catch {
    return '';
  }
}

export function ProposalCard({
  proposal,
  proposerName,
  proposerSlot = 'm-sage',
  approving,
  rejecting,
  onApprove,
  onReject,
}: ProposalCardProps): ReactElement {
  const { t } = useTranslation();
  const [points, setPoints] = useState(proposal.point_value);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  const busy = approving || rejecting;
  const name = proposerName || t('approvals.proposals.unknownKid');
  const cadence = recurrenceLabel(proposal.recurrence, t);

  return (
    <article
      data-testid={`proposal-card-${proposal.id}`}
      aria-label={t('approvals.row.proposal', { title: proposal.title })}
      className={FEATURED_CARD}
    >
      <header className="flex items-center gap-2.5">
        <InitialAvatar name={name} colorSlot={proposerSlot} size={36} decorative />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-body font-extrabold text-ink">
            {t('approvals.proposals.proposedBy', { name })}
          </p>
          {proposal.created_at ? (
            <p className="m-0 text-caption text-ink-2">
              <RelativeTime instant={proposal.created_at} />
            </p>
          ) : null}
        </div>
      </header>

      <div>
        <h2 className="m-0 text-xl font-extrabold leading-tight text-ink">
          {t('approvals.row.proposal', { title: proposal.title })}
        </h2>
        {proposal.description ? (
          <p className="m-0 mt-1 text-body-s text-ink-2">{proposal.description}</p>
        ) : null}
        {cadence ? <p className="m-0 mt-1 text-caption text-ink-2">{cadence}</p> : null}
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-body-s font-bold text-ink">
          {t('approvals.proposals.adjustPoints')}
        </span>
        <Stepper
          data-testid={`proposal-points-${proposal.id}`}
          label={t('approvals.proposals.adjustPoints')}
          value={points}
          min={1}
          size="md"
          suffix=" p"
          decreaseLabel={t('approvals.proposals.fewerPoints')}
          increaseLabel={t('approvals.proposals.morePoints')}
          onChange={setPoints}
        />
      </div>

      {showReject ? (
        <div className="flex flex-col gap-2" data-testid={`proposal-reject-${proposal.id}`}>
          <label className="flex flex-col gap-1">
            <span className="text-control font-extrabold text-ink">
              {t('approvals.proposals.rejectReason')}
            </span>
            <textarea
              data-testid={`proposal-reason-${proposal.id}`}
              className="field"
              maxLength={140}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size={44}
              className="flex-1"
              onClick={() => {
                setShowReject(false);
                setReason('');
              }}
              data-testid={`proposal-reject-cancel-${proposal.id}`}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid"
              size={44}
              className="flex-2"
              disabled={busy}
              onClick={() => onReject(proposal.id, reason.trim())}
              data-testid={`proposal-reject-confirm-${proposal.id}`}
            >
              {t('approvals.proposals.rejectConfirm')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2.5">
          <Button
            variant="secondary"
            size={52}
            className="flex-1"
            disabled={busy}
            onClick={() => setShowReject(true)}
            data-testid={`proposal-reject-open-${proposal.id}`}
          >
            {t('approvals.proposals.reject')}
          </Button>
          <Button
            variant="primary"
            size={52}
            className="flex-2"
            disabled={busy}
            onClick={() => onApprove(proposal.id, { point_value: points })}
            data-testid={`proposal-approve-${proposal.id}`}
          >
            <Icon.Check size={20} />
            {t('approvals.proposals.approve')}
          </Button>
        </div>
      )}
    </article>
  );
}
